import { useAppDispatch } from 'hooks/redux';
import _isNil from 'lodash/isNil';
import PropTypes from 'prop-types';
// CORE
import React, { useCallback } from 'react';
import { useStore } from 'react-redux';
import { v4 as uuid } from 'uuid';

import { getBusinessCoaIds } from 'app-components/flux/categories/categoriesSelectors';
import { getFeatureFlag } from 'app-components/flux/feature-flags/featureFlagsSelectors';
import { clearState } from 'app-components/flux/transaction-rules/transactionRulesForm';
import { RuleProvider } from 'app-components/flux/transaction-rules/transactionRulesFormState';
import { transactionRulesActions } from 'app-components/flux/transaction-rules/transactionRulesSlice';
import { ResolutionTypeEnum } from 'app-components/flux/transaction-rules/transactionRulesTypes';
// DATA
import { tracker } from 'app-components/utils/core';

import RuleContainer from 'containers/Settings/ChildPages/RulesSimplifi/RuleContainer';

export const DIALOG_TYPE_MEMORIZED_RULE_EDIT = 'DIALOG_TYPE_MEMORIZED_RULE_EDIT';

const MemorizedRuleEditDialog = React.memo((props) => {
  const { memorizedRule, fromTxn, onRuleCreated, editField } = props;
  const { inferredPayeeName, statementPayeeName, payee, coa, businessId } = memorizedRule;
  const store = useStore();

  let targetCOA = coa?.toJS?.() || coa;
  const txnRuleV2 = getFeatureFlag(store.getState(), 'qbpUsageTxnrulesv2');

  //TODO: we should have a app-components selector for this (can't now because of release pressure)
  const businessCoaIds = getBusinessCoaIds(store.getState());
  const isBusinessCoa = businessCoaIds && businessCoaIds.includes(targetCOA.id);

  const appDispatch = useAppDispatch();

  const handleOnUpdate = useCallback(
    (newRule) => {
      if (newRule) {
        const ifConditions = [];
        const thenConditions = [];

        if (newRule.payeeSourceCondition) {
          ifConditions.push('payee');
        }
        if (newRule.amountSourceCondition) {
          ifConditions.push('amount');
        }
        if (newRule.accountSourceIds) {
          ifConditions.push('accounts');
        }
        if (newRule.coaSourceConditions) {
          ifConditions.push('categories');
        }
        if (newRule.payeeTarget) {
          thenConditions.push('payee');
        }
        if (newRule.coaTargetCondition) {
          thenConditions.push('category');
        }
        if (newRule.tagsTargetIds) {
          thenConditions.push('tags');
        }
        if (!_isNil(newRule.isExcludedFromF2STarget) || _isNil(newRule.isExcludedFromReportsTarget)) {
          thenConditions.push('exclude');
        }
        if (!_isNil(newRule.isReviewedTarget)) {
          thenConditions.push('reviewed');
        }

        const ruleConditions = {
          if: ifConditions,
          then: thenConditions,
        };

        if (newRule?.id) {
          appDispatch(transactionRulesActions.update({ ...newRule, id: newRule.id }));
          tracker.track(tracker.events.txnRuleCompleted, {
            userAction: ResolutionTypeEnum.REPLACE,
            applyPast: newRule?.applyToExistingTransactions,
            rule: ruleConditions,
          });
        } else {
          appDispatch(transactionRulesActions.create({ ...newRule, clientId: uuid().toUpperCase() }));
          tracker.track(tracker.events.txnRuleCompleted, {
            userAction: ResolutionTypeEnum.REPLACE,
            applyPast: newRule?.applyToExistingTransactions,
            rule: ruleConditions,
          });
        }
      }
      onRuleCreated?.(newRule);
    },
    [appDispatch, onRuleCreated],
  );

  const newRule = {
    ...clearState,
    payeeTarget: payee,
    payeeSourceCondition: {
      payeeType: 'STATEMENT', // default to Statement name
      payeeMatchingCriteria: [{ payeeName: statementPayeeName, payeeMatchingOperator: 'CONTAINS' }], // default to Contains
    },
    ...(isBusinessCoa && txnRuleV2
      ? { businessCoaTargetCondition: targetCOA, businessTargetId: businessId }
      : { coaTargetCondition: targetCOA }),
    ...(!txnRuleV2 && businessId && businessId !== '' && { businessTargetId: businessId }),
  };

  return (
    <RuleProvider
      rule={newRule}
      onUpdate={handleOnUpdate}
      statementPayeeName={statementPayeeName}
      inferredPayeeName={inferredPayeeName}
      fromTxn={fromTxn}
      editField={editField}
    >
      <RuleContainer />
    </RuleProvider>
  );
});

MemorizedRuleEditDialog.propTypes = {
  memorizedRule: PropTypes.object,
  onClose: PropTypes.func,
  onRuleCreated: PropTypes.func,
  open: PropTypes.bool, // set manually for controlled dialog
  dialogId: PropTypes.string, // set automatically for uncontrolled dialog
  fromTxn: PropTypes.bool,
  editField: PropTypes.string,
};

MemorizedRuleEditDialog.displayName = 'MemorizedRuleEditDialog';

export default MemorizedRuleEditDialog;
